import type { HTMLAttributes, ReactNode } from 'react';
import { Stack } from '@mantine/core';
import { Split } from '@ayocore/platform/client/components/Split';

export interface DriveLayoutProps {
  /** Левая панель — дерево папок. */
  tree: ReactNode;
  /** Правая панель — крошки, список, оверлеи. */
  content: ReactNode;
  /**
   * Ключ localStorage для положения разделителя. У каждого экрана свой:
   * пользователь настраивает ширину дерева отдельно на странице и в модалке.
   */
  storageKey: string;
  /** Стартовая доля левой панели в процентах. */
  defaultLeftPercent?: number;
  /** Минимальная доля левой панели — ниже дерево нечитаемо. */
  minPercent?: number;
  /**
   * Пропсы контейнера правой панели: сюда идут `dropHandlers` у экранов с
   * загрузкой. Read-only экраны (файл-пикер) их не передают.
   */
  contentProps?: HTMLAttributes<HTMLDivElement>;
}

/**
 * DriveLayout — каркас «проводника»: дерево папок слева, содержимое справа,
 * между ними перетаскиваемый разделитель.
 *
 * Только раскладка, никакого поведения: что показывать в панелях и что делает
 * клик — решает вызывающий. Поэтому каркас переиспользуют экраны с разной
 * семантикой — `DriveExplorer` (клик открывает) и `DriveFilePickerModal`
 * (клик выделяет).
 *
 * Правая панель — `position: relative`: на ней позиционируется оверлей
 * перетаскивания файлов (`DriveDropOverlay`).
 */
export function DriveLayout({
  tree,
  content,
  storageKey,
  defaultLeftPercent = 22,
  minPercent = 12,
  contentProps,
}: DriveLayoutProps) {
  return (
    <Split
      storageKey={storageKey}
      defaultLeftPercent={defaultLeftPercent}
      minPercent={minPercent}
      maxPercent={45}
      style={{ flex: 1, minHeight: 0, minWidth: 0, width: '100%' }}
    >
      <Split.Pane>
        <Stack gap='xs' h='100%' pr={8} style={{ minHeight: 0 }}>
          {tree}
        </Stack>
      </Split.Pane>

      <Split.Resizer
        style={{
          '--split-resizer-color': 'var(--mantine-color-gray-3)',
          '--split-resizer-size': '3px',
        }}
      />

      <Split.Pane>
        <div
          style={{
            height: '100%',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            paddingLeft: 8,
          }}
          {...contentProps}
        >
          <Stack gap='xs' h='100%' style={{ minHeight: 0, position: 'relative' }}>
            {content}
          </Stack>
        </div>
      </Split.Pane>
    </Split>
  );
}
